/* AlertSummaryWidget
   Clinical Dashboard's alert summary + nav-into-Triage surface, per
   AbdulRafiu's Q3 decision: "The Clinical Dashboard should provide an
   alert summary/counts and navigation into Triage rather than
   unnecessarily duplicate the full operational alert workflow."

   Deliberately self-contained (own fetch, own state) rather than folded
   into useClinicalOverviewStats — that hook's current contents haven't
   been reviewed, so this is additive rather than a blind edit to
   existing wiring. If useClinicalOverviewStats should own this data
   instead, that's a targeted follow-up once its source is available.

   ASSUMPTION FLAGGED: uses react-router's useNavigate to link to
   /dashboard/triage, consistent with the route structure already
   confirmed (App.jsx routes). Verify react-router-dom is the routing
   library in use if this doesn't match.
*/
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as FiIcons from "react-icons/fi";
const { FiAlertTriangle, FiChevronRight } = FiIcons;
import SafeIcon from "../common/SafeIcon";
import api from "../lib/api";

const SEVERITY_STYLES = {
  critical: "bg-rose-500/10 border-rose-500/30 text-rose-400",
  high: "bg-orange-500/10 border-orange-500/30 text-orange-400",
  medium: "bg-amber-500/10 border-amber-500/30 text-amber-400",
  low: "bg-slate-500/10 border-slate-500/30 text-slate-400",
};

export default function AlertSummaryWidget() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/alerts/summary");
        if (!cancelled) setSummary(res.data);
      } catch (err) {
        if (!cancelled) setError("Failed to load alert summary");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-3xl p-4 lg:p-6 backdrop-blur-xl">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-rose-600/20 rounded-2xl flex items-center justify-center">
            <SafeIcon icon={FiAlertTriangle} className="text-rose-400 text-xl" />
          </div>
          <div>
            <h2 className="text-base lg:text-lg font-bold">Active Alerts</h2>
            <p className="text-xs text-slate-400">Tenant-wide, all patients</p>
          </div>
        </div>
        <button
          onClick={() => navigate("/dashboard/triage")}
          className="flex items-center gap-1 text-xs font-bold text-slate-400 hover:text-white"
        >
          Open Triage <SafeIcon icon={FiChevronRight} />
        </button>
      </div>

      {isLoading && <p className="text-sm text-slate-500">Loading…</p>}
      {error && <p className="text-sm text-rose-400">{error}</p>}

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {["critical", "high", "medium", "low"].map((sev) => (
            <div key={sev} className={`rounded-2xl border p-3 ${SEVERITY_STYLES[sev]}`}>
              <div className="text-[10px] font-black uppercase tracking-widest">{sev}</div>
              <div className="text-2xl font-black">{summary[sev] ?? 0}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}